class BuildYourBundle extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      quickAddSelectorBundle: '[js-quick-add-bundle]',
      quickAddBundleAtc: '[js-add-bundle-to-cart]',
      bundleConfig: '[js-bundle-config]',
      bundleCount: '[js-bundle-count]',
      bundleOriginalPrice: '[js-bundle-original-price]',
      bundleDiscountedPrice: '[js-bundle-discounted-price]',
      bundlePlaceholder: '[js-bundle-placeholder]',
      progressStep: '[js-bundle-progress-step]',
      stepStatus: '[js-bundle-step-status]',
      stepStatusLabel: '[js-bundle-step-status-label]',
      addLabel: '[js-bundle-add-label]',
      accordionItem: '[js-accordion-item]',
      productCardBundle: '[js-product-card-bundle]',
      bundleRemoveSelection: '[js-bundle-remove-selection]',
      bundleAtcStatus: '[js-bundle-atc-status]',
      bundleSelectionCrawl: '[js-bundle-selection-crawl]',
      accordionHeader: '[js-accordion-header]',
    };

    this._stepConfig = [];
    this._discountPercent = 0;
    this._selections = new Map();
    this._firedBuilderComplete = false;
    this._prevStepAdvanceFlags = null;
    this._optionalStepRegressed = new Set();
  }

  connectedCallback() {
    this._parseConfig();
    this._initSelectionState();

    const quickAddButtons = this.querySelectorAll(this._selectors.quickAddSelectorBundle);
    quickAddButtons.forEach((button) => {
      button.addEventListener('click', (evt) => {
        this._handleBundleProductAdd(evt);
      });
    });

    this.addEventListener('click', (evt) => {
      this._onBundleRemoveClick(evt);
      const quickAdd = evt.target.closest(this._selectors.quickAddSelectorBundle);
      const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
      if (!quickAdd && !removeBtn) {
        const pdpLink = evt.target.closest('a[href]');
        if (pdpLink && pdpLink.closest(this._selectors.productCardBundle)) {
          evt.preventDefault();
        }
      }
      if (evt.target.closest(this._selectors.accordionHeader)) {
        requestAnimationFrame(() => this._syncProgress());
      }
    });

    this.addEventListener(
      'keydown',
      (evt) => {
        if (evt.key !== 'Enter' && evt.key !== ' ') return;
        const quickAdd = evt.target.closest(this._selectors.quickAddSelectorBundle);
        const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
        if (quickAdd || removeBtn) return;
        const pdpLink = evt.target.closest('a[href]');
        if (pdpLink && pdpLink.closest(this._selectors.productCardBundle)) {
          evt.preventDefault();
        }
      },
      true
    );

    const bundleAtc = this.querySelector(this._selectors.quickAddBundleAtc);
    if (bundleAtc) {
      bundleAtc.addEventListener('click', (evt) => {
        this._handleBundleAddToCart(evt);
      });
    }

    this._syncBundle();
  }

  /* Config */

  _parseConfig() {
    const configEl = this.querySelector(this._selectors.bundleConfig);
    if (!configEl) return;

    try {
      const parsedConfig = JSON.parse(configEl.textContent || '{}');
      this._discountPercent = parseFloat(parsedConfig.discountPercent) || 0;
      this._stepConfig = (parsedConfig.steps || []).map((jsonStep) => ({
        blockId: String(jsonStep.blockId),
        selectionSize: parseInt(jsonStep.selectionSize, 10) || 0,
        mandatory: jsonStep.mandatory !== false,
      }));
    } catch (error) {
      console.error('BuildYourBundle: invalid bundle config', error);
    }
  }

  _initSelectionState() {
    this._stepConfig.forEach((step) => {
      if (!this._selections.has(step.blockId)) {
        this._selections.set(step.blockId, []);
      }
    });
  }

  /* Steps: requirements & progress chain */

  _getStepSelectedCount(step) {
    return (this._selections.get(step.blockId) || []).length;
  }

  _isStepRequirementMet(step) {
    if (!step.mandatory) return true;
    return this._getStepSelectedCount(step) >= step.selectionSize;
  }

  _isStepVisualComplete(step) {
    const count = this._getStepSelectedCount(step);
    if (!step.mandatory) {
      return count > 0;
    }
    return count >= step.selectionSize;
  }

  _trackOptionalStepRegression(step, prevCount, nextCount) {
    if (!step || step.mandatory) return;
    const id = String(step.blockId);
    if (prevCount > 0 && nextCount === 0) {
      this._optionalStepRegressed.add(id);
    } else if (nextCount > 0) {
      this._optionalStepRegressed.delete(id);
    }
  }

  _stepPassesForProgress(step) {
    if (step.mandatory) {
      return this._isStepRequirementMet(step);
    }
    const count = this._getStepSelectedCount(step);
    if (count > 0) return true;
    return !this._optionalStepRegressed.has(String(step.blockId));
  }

  _stepsBeforeIndexAllPass(stepIndex) {
    for (let i = 0; i < stepIndex; i++) {
      if (!this._stepPassesForProgress(this._stepConfig[i])) return false;
    }
    return true;
  }

  /* Quick add */

  _handleBundleProductAdd(evt) {
    evt.preventDefault();
    const button = evt.currentTarget;

    if (button.disabled) return;

    const blockId = button.dataset.bundleBlockId;
    if (!blockId) return;

    const step = this._stepConfig.find((configStep) => configStep.blockId === blockId);
    if (!step || step.selectionSize < 1) return;

    const variantId = button.dataset.variantId;
    const variantPrice = parseInt(button.dataset.variantPrice || '0', 10);
    const productTitle = button.dataset.productTitle || '';
    const productCard = button.closest(this._selectors.productCardBundle);

    if (!variantId) return;

    const prevCount = (this._selections.get(blockId) || []).length;
    let selections = [...(this._selections.get(blockId) || [])];
    const existingIndex = selections.findIndex((selection) => selection.variantId === variantId);

    if (existingIndex >= 0) {
      selections = selections.filter((_selection, selectionIndex) => selectionIndex !== existingIndex);
    } else {
      if (selections.length >= step.selectionSize) {
        selections = selections.slice(0, -1);
      }
      selections.push({
        variantId: variantId,
        priceCents: variantPrice,
        title: productTitle,
        card: productCard,
        productUrl: button.dataset.productUrl || '',
      });
    }

    this._trackOptionalStepRegression(step, prevCount, selections.length);
    this._selections.set(blockId, selections);
    this._expandBundleAccordion(blockId, { closeSiblings: false });
    this._syncBundle();
  }

  /* Cart */

  _getCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  getSectionsToRender() {
    const cart = this._getCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender();
    }
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-qty',
        section: 'cart-icon-qty',
      },
    ];
  }

  _getBundleDiscountRatio() {
    return Math.min(Math.max(this._discountPercent, 0), 100) / 100;
  }

  _getBundleCartItems() {
    const bundleGroupId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `bg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;

    const discountRatio = this._getBundleDiscountRatio();
    const lineMap = new Map();

    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      selections.forEach((selection) => {
        const id = parseInt(selection.variantId, 10);
        if (isNaN(id)) return;
        const origCents = selection.priceCents || 0;
        const discountedUnit = Math.round(origCents * (1 - discountRatio));
        if (!lineMap.has(id)) {
          lineMap.set(id, { qty: 0, sumOrig: 0, sumDiscounted: 0 });
        }
        const entry = lineMap.get(id);
        entry.qty += 1;
        entry.sumOrig += origCents;
        entry.sumDiscounted += discountedUnit;
      });
    });

    return [...lineMap.entries()].map(([variantId, data]) => {
      const q = data.qty;
      const unitDiscounted = q > 0 ? Math.round(data.sumDiscounted / q) : 0;
      const unitOrig = q > 0 ? Math.round(data.sumOrig / q) : 0;
      return {
        id: variantId,
        quantity: q,
        properties: {
          _bundle_builder: 'true',
          _bundle_group_id: bundleGroupId,
          _bundle_unit_price_cents: String(unitDiscounted),
          _bundle_unit_compare_cents: String(unitOrig),
        },
      };
    });
  }

  _getBuilderEstimatedTotalCents() {
    let totalCents = 0;
    this._selections.forEach((selections) => {
      selections.forEach((selection) => {
        totalCents += selection.priceCents;
      });
    });
    const discountRatio = Math.min(Math.max(this._discountPercent, 0), 100) / 100;
    return Math.round(totalCents * (1 - discountRatio));
  }

  _allocateBundleCentsFromCart(cartItems, requestedItems) {
    const need = new Map();
    requestedItems.forEach((lineItem) => {
      const variantId = lineItem.id;
      need.set(variantId, (need.get(variantId) || 0) + lineItem.quantity);
    });
    let cents = 0;
    (cartItems || []).forEach((cartLine) => {
      const variantId = cartLine.variant_id;
      let remaining = need.get(variantId) || 0;
      if (remaining <= 0) return;
      const take = Math.min(remaining, cartLine.quantity);
      const perUnit = cartLine.quantity > 0 ? cartLine.final_line_price / cartLine.quantity : 0;
      cents += Math.round(perUnit * take);
      need.set(variantId, remaining - take);
    });
    return cents;
  }

  /* Crawlable summary & dataLayer */

  _syncCrawlableSelectionSummary() {
    const host = this.querySelector(this._selectors.bundleSelectionCrawl);
    if (!host) return;
    host.innerHTML = '';
    const orderedSelections = [];
    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      selections.forEach((selection) => orderedSelections.push(selection));
    });
    if (!orderedSelections.length) {
      const emptyMessage = document.createElement('p');
      emptyMessage.textContent = 'Current bundle selection: none.';
      host.appendChild(emptyMessage);
      return;
    }
    const intro = document.createElement('p');
    intro.textContent = 'Current bundle selection (updates as you build):';
    host.appendChild(intro);
    const orderedList = document.createElement('ol');
    orderedSelections.forEach((selection) => {
      const listItem = document.createElement('li');
      const productUrl = selection.productUrl || '';
      if (productUrl) {
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = selection.title || 'Product';
        listItem.appendChild(link);
      } else {
        listItem.textContent = selection.title || 'Product';
      }
      orderedList.appendChild(listItem);
    });
    host.appendChild(orderedList);
  }

  _publishBundleBuilderComplete() {
    const allComplete = this._stepsCompleteFlags().every(Boolean);
    if (!allComplete) {
      this._firedBuilderComplete = false;
      return;
    }
    if (this._firedBuilderComplete) return;
    this._firedBuilderComplete = true;
    window.dataLayer = window.dataLayer || [];
    let itemSlotsFilled = 0;
    this._selections.forEach((selections) => {
      itemSlotsFilled += selections.length;
    });
    window.dataLayer.push({
      event: 'bundle_builder_complete',
      bundle_discount_percent_shown: this._discountPercent,
      bundle_item_slots_filled: itemSlotsFilled,
    });
  }

  /* Bundle ATC UI */

  _getBundleAtcStatusEl() {
    return this.querySelector(this._selectors.bundleAtcStatus);
  }

  _clearBundleAtcMessage() {
    const statusEl = this._getBundleAtcStatusEl();
    if (!statusEl) return;
    statusEl.textContent = '';
    statusEl.classList.add('hidden');
    statusEl.classList.remove('section-byb__atc-message--error');
    statusEl.setAttribute('aria-live', 'polite');
  }

  _setBundleAtcError(message) {
    const statusEl = this._getBundleAtcStatusEl();
    if (!statusEl) return;
    const text = message || window.cartStrings?.error || 'Something went wrong. Please try again.';
    statusEl.textContent = text;
    statusEl.classList.remove('hidden');
    statusEl.classList.add('section-byb__atc-message--error');
    statusEl.setAttribute('aria-live', 'assertive');
  }

  _publishBundleAddToDataLayer(items, cartPayload) {
    window.dataLayer = window.dataLayer || [];
    const variantIds = items.map((lineItem) => String(lineItem.id));
    const lineCount = items.reduce((sum, lineItem) => sum + (lineItem.quantity || 0), 0);
    const allocatedLineCents =
      cartPayload && cartPayload.items
        ? this._allocateBundleCentsFromCart(cartPayload.items, items)
        : undefined;
    const builderEstimateCents = this._getBuilderEstimatedTotalCents();
    window.dataLayer.push({
      event: 'bundle_add_to_cart',
      bundle_variant_ids: variantIds,
      bundle_distinct_variants: variantIds.length,
      bundle_line_quantity: lineCount,
      bundle_discount_percent_shown: this._discountPercent,
      cart_item_count: typeof cartPayload.item_count === 'number' ? cartPayload.item_count : undefined,
      bundle_builder_estimate_cents: builderEstimateCents,
      bundle_cart_allocated_line_cents: typeof allocatedLineCents === 'number' ? allocatedLineCents : undefined,
      bundle_pricing_matches_estimate:
        typeof allocatedLineCents === 'number'
          ? Math.abs(allocatedLineCents - builderEstimateCents) <= 2
          : undefined,
    });
  }

  _handleBundleAddToCart(evt) {
    evt.preventDefault();
    const bundleAtc = evt.currentTarget;

    if (bundleAtc.disabled || bundleAtc.getAttribute('aria-disabled') === 'true') return;

    const items = this._getBundleCartItems();
    if (!items.length) return;

    this._clearBundleAtcMessage();
    this.cart = this._getCart();
    bundleAtc.classList.add('loading');
    bundleAtc.setAttribute('aria-disabled', 'true');
    const spinner = bundleAtc.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const data = {
      items,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      ...fetchConfig(),
      body: JSON.stringify(data),
    })
      .then((fetchResponse) => fetchResponse.json())
      .then((cartResponse) => {
        if (cartResponse.status) {
          const errorMessage =
            cartResponse.message ||
            (cartResponse.description && String(cartResponse.description)) ||
            (cartResponse.errors && String(cartResponse.errors));
          this._setBundleAtcError(errorMessage);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'build-your-bundle',
            message: cartResponse.message,
            errors: cartResponse.errors || cartResponse.description,
          });
          return;
        }

        this._clearBundleAtcMessage();
        this._publishBundleAddToDataLayer(items, cartResponse);

        if (!this.cart) {
          window.location.href = window.routes.cart_url;
          return;
        }

        this.cart.renderContents(cartResponse);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'build-your-bundle',
          cartData: cartResponse,
        });
      })
      .catch((error) => {
        console.error(error);
        this._setBundleAtcError(window.cartStrings?.error);
      })
      .finally(() => {
        bundleAtc.classList.remove('loading');
        bundleAtc.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
        this._handleButtonLabel();
      });
  }

  /* Remove from bundle */

  _onBundleRemoveClick(evt) {
    const removeBtn = evt.target.closest(this._selectors.bundleRemoveSelection);
    if (!removeBtn || !this.contains(removeBtn)) return;

    evt.preventDefault();
    evt.stopPropagation();

    const blockId = removeBtn.dataset.bundleBlockId;
    const variantId = removeBtn.dataset.bundleVariantId;
    if (!blockId || !variantId) return;

    this._removeBundleSelection(blockId, variantId);
  }

  _removeBundleSelection(blockId, variantId) {
    const step = this._stepConfig.find((s) => s.blockId === String(blockId));
    const selections = [...(this._selections.get(blockId) || [])];
    const prevCount = selections.length;
    const nextSelections = selections.filter((selection) => selection.variantId !== variantId);
    this._trackOptionalStepRegression(step, prevCount, nextSelections.length);
    this._selections.set(blockId, nextSelections);
    this._syncBundle();
  }

  _bundleRemoveIconMarkup() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-bundle-close" viewBox="5.5 5.5 13 13" aria-hidden="true"><path stroke="currentColor" stroke-width="1.65" stroke-linecap="round" d="M7.35 7.35 16.65 16.65M16.65 7.35 7.35 16.65"/></svg>';
  }

  _createBundleRemoveButton(blockId, variantId) {
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.setAttribute('js-bundle-remove-selection', '');
    removeButton.className = 'section-byb__bundle-remove';
    removeButton.dataset.bundleBlockId = blockId;
    removeButton.dataset.bundleVariantId = variantId;
    removeButton.setAttribute('aria-label', 'Remove from bundle');
    removeButton.innerHTML = this._bundleRemoveIconMarkup();
    return removeButton;
  }

  _getProductImageClone(card) {
    if (!card) return null;
    const productImage = card.querySelector('.card__media img');
    if (!productImage) return null;
    const clone = productImage.cloneNode(true);
    if (clone instanceof HTMLImageElement) {
      clone.removeAttribute('loading');
    }
    return clone;
  }

  /* Sync UI */

  _syncButtonLabelsFromState() {
    this.querySelectorAll(this._selectors.quickAddSelectorBundle).forEach((addButton) => {
      const blockId = addButton.dataset.bundleBlockId;
      const variantId = addButton.dataset.variantId;
      if (!blockId || !variantId) return;
      const selections = this._selections.get(blockId) || [];
      const isSelected = selections.some((selection) => selection.variantId === variantId);
      this._handleButtonAddedLabel(addButton, isSelected);
    });
  }

  _handleButtonAddedLabel(button, isAdded) {
    const label = button.querySelector(this._selectors.addLabel);
    if (!label) return;

    if (isAdded) {
      label.textContent = 'Remove From Bundle';
    } else if (!button.disabled) {
      label.textContent = 'Add To Bundle';
    }
  }

  _syncPlaceholders() {
    this.querySelectorAll(`${this._selectors.bundlePlaceholder}[data-bundle-block-id]`).forEach((placeholder) => {
      const blockId = placeholder.dataset.bundleBlockId;
      const slotIndex = parseInt(placeholder.dataset.bundleSlotIndex || '1', 10) - 1;
      if (!blockId || isNaN(slotIndex)) return;

      const selections = this._selections.get(blockId) || [];
      const selection = selections[slotIndex];
      placeholder.innerHTML = '';

      if (selection) {
        const productImage = this._getProductImageClone(selection.card);
        if (productImage) placeholder.appendChild(productImage);
      }
    });
  }

  _syncCardRemoveOnMedia() {
    this.querySelectorAll('.bundle-card').forEach((bundleCard) => {
      const addBtn = bundleCard.querySelector(this._selectors.quickAddSelectorBundle);
      const blockId = addBtn && addBtn.dataset.bundleBlockId;
      const variantId = addBtn && addBtn.dataset.variantId;
      const media = bundleCard.querySelector('.card__media');
      if (!media || !blockId || !variantId) return;

      const selections = this._selections.get(blockId) || [];
      const isSelected = selections.some((selection) => selection.variantId === variantId);
      const existingRemoveBtn = media.querySelector(this._selectors.bundleRemoveSelection);

      if (!isSelected) {
        if (existingRemoveBtn) existingRemoveBtn.remove();
        return;
      }

      if (!existingRemoveBtn) {
        media.appendChild(this._createBundleRemoveButton(blockId, variantId));
      } else {
        existingRemoveBtn.dataset.bundleBlockId = blockId;
        existingRemoveBtn.dataset.bundleVariantId = variantId;
      }
    });
  }

  _isVariantSelectedInOtherStep(variantId, blockId) {
    const vid = String(variantId);
    const bid = String(blockId);
    return this._stepConfig.some((s) => {
      if (String(s.blockId) === bid) return false;
      return (this._selections.get(s.blockId) || []).some((sel) => String(sel.variantId) === vid);
    });
  }

  _syncCrossStepProductVisibility() {
    this.querySelectorAll(this._selectors.productCardBundle).forEach((cardRoot) => {
      const bundleCard = cardRoot.closest('.bundle-card');
      const addBtn = cardRoot.querySelector(this._selectors.quickAddSelectorBundle);
      if (!bundleCard || !addBtn) return;
      const blockId = addBtn.dataset.bundleBlockId;
      const variantId = addBtn.dataset.variantId;
      if (!blockId || !variantId) return;
      const hideCard = this._isVariantSelectedInOtherStep(variantId, blockId);
      bundleCard.toggleAttribute('hidden', hideCard);
      bundleCard.setAttribute('aria-hidden', hideCard ? 'true' : 'false');
    });
  }

  _syncCardBackgrounds() {
    this.querySelectorAll('.bundle-card').forEach((bundleCard) => {
      const addButton = bundleCard.querySelector(this._selectors.quickAddSelectorBundle);
      const blockId = addButton && addButton.dataset.bundleBlockId;
      if (!blockId) return;

      const selectionColor = bundleCard.dataset.bundleSelectionColor || '';
      const variantId = addButton.dataset.variantId;
      const selections = this._selections.get(blockId) || [];
      const isSelected = variantId && selections.some((selection) => selection.variantId === variantId);

      if (isSelected) {
        bundleCard.classList.add('is-bundle-selected');
        bundleCard.style.backgroundColor = selectionColor;
      } else {
        bundleCard.classList.remove('is-bundle-selected');
        bundleCard.style.backgroundColor = '';
      }
    });
  }

  /* Progress */

  _syncProgress() {
    const activeBlockId = this._resolveProgressActiveBlockId();

    for (const step of this._stepConfig) {
      const progressStepEl = this._findElementByBundleBlockId(this._selectors.progressStep, step.blockId);
      if (!progressStepEl) continue;

      const isStepVisuallyComplete = this._isStepVisualComplete(step);
      const isCurrentStep =
        activeBlockId !== null && String(step.blockId) === String(activeBlockId);

      const showProgressFill = this._getStepSelectedCount(step) > 0;

      progressStepEl.classList.toggle('is-bundle-step-complete', isStepVisuallyComplete);
      progressStepEl.classList.toggle('is-bundle-step-current', isCurrentStep);

      if (showProgressFill) {
        const selectionColor = progressStepEl.dataset.bundleSelectionColor || '';
        progressStepEl.style.setProperty('--bundle-progress-fill', selectionColor);
        progressStepEl.style.backgroundColor = selectionColor;
      } else {
        progressStepEl.style.removeProperty('--bundle-progress-fill');
        progressStepEl.style.backgroundColor = '';
      }
    }
  }

  /* Accordion status */

  _syncAccordionStatus() {
    this._stepConfig.forEach((step) => {
      const selections = this._selections.get(step.blockId) || [];
      const accordionItem = this._findElementByBundleBlockId(this._selectors.accordionItem, step.blockId);
      if (!accordionItem) return;

      const status = accordionItem.querySelector(this._selectors.stepStatus);
      const label = accordionItem.querySelector(this._selectors.stepStatusLabel);
      const selectedCount = selections.length;
      const slotsRequired = step.selectionSize;

      if (label) {
        label.textContent = selectedCount + ' of ' + slotsRequired + ' selected';
      }

      if (status) {
        if (this._isStepVisualComplete(step)) {
          status.classList.add('is-bundle-step-complete');
        } else {
          status.classList.remove('is-bundle-step-complete');
        }
      }
    });
  }

  /* Totals */

  _formatMoney(cents) {
    const format = this.dataset.moneyFormat;
    if (typeof theme !== 'undefined' && theme.utils && typeof theme.utils.formatMoney === 'function') {
      return theme.utils.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  _syncTotals() {
    let totalCents = 0;
    let itemCount = 0;

    this._selections.forEach((selections) => {
      selections.forEach((selection) => {
        totalCents += selection.priceCents;
        itemCount += 1;
      });
    });

    const discountRatio = Math.min(Math.max(this._discountPercent, 0), 100) / 100;
    const discountedCents = Math.round(totalCents * (1 - discountRatio));

    const countEl = this.querySelector(this._selectors.bundleCount);
    const originalEl = this.querySelector(this._selectors.bundleOriginalPrice);
    const discountedEl = this.querySelector(this._selectors.bundleDiscountedPrice);

    if (countEl) {
      countEl.textContent = String(itemCount);
    }

    if (discountedEl) {
      discountedEl.innerHTML = this._formatMoney(discountRatio > 0 ? discountedCents : totalCents);
    }

    if (originalEl) {
      if (totalCents > 0 && discountRatio > 0) {
        originalEl.hidden = false;
        originalEl.innerHTML = this._formatMoney(totalCents);
      } else {
        originalEl.hidden = true;
        originalEl.innerHTML = '';
      }
    }
  }

  _handleButtonLabel() {
    const bundleAtc = this.querySelector(this._selectors.quickAddBundleAtc);
    if (!bundleAtc) return;
    bundleAtc.disabled = !this._stepsCompleteFlags().every(Boolean);
  }

  _stepsCompleteFlags() {
    return this._stepConfig.map((step) => this._isStepRequirementMet(step));
  }

  /* DOM & accordion */

  _findElementByBundleBlockId(baseSelector, blockId) {
    const id = String(blockId);
    return Array.from(this.querySelectorAll(baseSelector)).find(
      (element) => element.dataset.bundleBlockId === id
    );
  }

  _getBundleAccordion(blockId) {
    return this._findElementByBundleBlockId(this._selectors.accordionItem, blockId);
  }

  _getBundleStickyOffsetPx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    if (raw.endsWith('px')) {
      const n = parseFloat(raw);
      if (Number.isFinite(n)) return n + 12;
    }
    const headerEl = document.querySelector('.shopify-section-header-sticky, .shopify-section-header');
    if (headerEl && headerEl.offsetHeight) {
      return headerEl.offsetHeight + 12;
    }
    return 12;
  }

  _scrollBundleStepHeaderToTop(element, settleInstant = false) {
    if (!element) return;
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = document.scrollingElement || document.documentElement;
    const top = Math.max(
      0,
      root.scrollTop + element.getBoundingClientRect().top - this._getBundleStickyOffsetPx()
    );
    const behavior = prefersReduced || settleInstant ? 'auto' : 'smooth';
    try {
      root.scrollTo({ top, behavior });
    } catch (_) {
      root.scrollTop = top;
    }
  }

  _getOpenAccordionBlockId() {
    let lastOpen = null;
    for (const step of this._stepConfig) {
      const accordionItem = this._getBundleAccordion(step.blockId);
      if (!accordionItem) continue;
      const header = accordionItem.querySelector(this._selectors.accordionHeader);
      if (header && header.getAttribute('aria-expanded') === 'true') {
        lastOpen = step.blockId;
      }
    }
    return lastOpen;
  }

  _resolveProgressActiveBlockId() {
    const failIdx = this._stepConfig.findIndex((s) => !this._stepPassesForProgress(s));
    const firstBad = failIdx === -1 ? this._stepConfig.length : failIdx;
    const openId = this._getOpenAccordionBlockId();

    if (openId) {
      const openIdx = this._stepConfig.findIndex((s) => String(s.blockId) === String(openId));
      if (openIdx >= 0 && this._stepsBeforeIndexAllPass(openIdx)) {
        return openId;
      }
    }

    if (firstBad < this._stepConfig.length) {
      return this._stepConfig[firstBad].blockId;
    }

    const last = this._stepConfig[this._stepConfig.length - 1];
    return last ? last.blockId : null;
  }

  _collapseBundleAccordion(blockId) {
    const accordionItem = this._getBundleAccordion(blockId);
    if (!accordionItem) return;
    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    if (!header || header.getAttribute('aria-expanded') === 'false') return;
    const controlsId = header.getAttribute('aria-controls');
    const accordionContent = controlsId ? document.getElementById(controlsId) : null;
    if (!accordionContent) return;
    header.setAttribute('aria-expanded', 'false');
    accordionContent.setAttribute('aria-hidden', 'true');
  }

  _expandBundleAccordion(blockId, options = {}) {
    const closeSiblings = options.closeSiblings !== false;
    const id = String(blockId);
    const accordionItem = this._getBundleAccordion(id);
    if (!accordionItem) return false;

    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    if (!header) return false;

    const controlsId = header.getAttribute('aria-controls');
    const accordionContent = controlsId ? document.getElementById(controlsId) : null;
    if (!accordionContent) return false;

    if (closeSiblings) {
      this._stepConfig.forEach((step) => {
        if (step.blockId !== id) {
          this._collapseBundleAccordion(step.blockId);
        }
      });
    }

    if (header.getAttribute('aria-expanded') !== 'true') {
      header.setAttribute('aria-expanded', 'true');
      if (typeof theme !== 'undefined' && theme.utils && typeof theme.utils.prepareTransition === 'function') {
        theme.utils.prepareTransition(accordionContent);
      }
      accordionContent.setAttribute('aria-hidden', 'false');
    }

    return true;
  }

  _scrollBundleStepProductsIntoView(blockId) {
    const accordionItem = this._getBundleAccordion(blockId);
    if (!accordionItem) return;
    const header = accordionItem.querySelector(this._selectors.accordionHeader);
    const target = header || accordionItem;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => this._scrollBundleStepHeaderToTop(target), 50);
        if (!prefersReduced) {
          setTimeout(() => this._scrollBundleStepHeaderToTop(target, true), 320);
        }
      });
    });
  }

  /* Advance to next step when a step completes */

  _handleStepComplete() {
    const flagsNow = this._stepConfig.map((s) =>
      s.mandatory ? this._isStepRequirementMet(s) : this._isStepVisualComplete(s)
    );
    const flagsPrev = this._prevStepAdvanceFlags;
    this._prevStepAdvanceFlags = flagsNow;
    if (!flagsPrev) return;

    const completedStepIndex = flagsNow.findIndex(
      (isCompleteNow, stepIndex) => isCompleteNow && !flagsPrev[stepIndex]
    );
    const nextStep =
      completedStepIndex >= 0 ? this._stepConfig[completedStepIndex + 1] : null;
    if (nextStep) {
      this._expandBundleAccordion(nextStep.blockId, { closeSiblings: false });
      this._scrollBundleStepProductsIntoView(nextStep.blockId);
    }
  }

  /* Sync */

  _syncBundle() {
    this._syncButtonLabelsFromState();
    this._syncPlaceholders();
    this._syncCrossStepProductVisibility();
    this._syncCardBackgrounds();
    this._syncCardRemoveOnMedia();
    this._handleStepComplete();
    this._syncProgress();
    this._syncAccordionStatus();
    this._syncTotals();
    this._handleButtonLabel();
    this._syncCrawlableSelectionSummary();
    this._publishBundleBuilderComplete();
  }
}

customElements.define('s-bundle-section', BuildYourBundle);
